import {
  EIconButtonSize,
  EIconButtonVariant,
  EToastVariant,
  GeneralCaretLeftIcon,
  GeneralEditEdit2Icon,
  GeneralEllipsesHorizontalIcon,
  GeneralLockLockIcon,
  GeneralMinusSquareIcon,
  GeneralTrashIcon,
  GeneralUserUsersIcon,
  IconButton,
  LinkButton,
  Popover,
  RawButton,
  TPopoverTriggerProps,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import { forwardRef, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useRouter } from '@/hooks/useRouter';
import { useToast } from '@/hooks/useToast';
import { trackWishlistEditedEvent } from '@/services/analytics/wishlists';
import apiRequest from '@/services/apiRequest';
import { getCoreApi } from '@/utility/getCoreApi';
import {
  canDeleteWishlist,
  canEditDetailsOfWishlist,
  canEditPublicStatusOfWishlist,
  canLeaveWishlist,
} from '@/utility/wishlists';

type TMobileActionsProps = {
  userRole: string | null;
  wishlistId?: number;
  editMode?: boolean;
  setEditMode?: (editMode: boolean) => void;
  setOpenRenameModal?: (openRenameModal: boolean) => void;
  setOpenDeleteModal?: (openDeleteModal: boolean) => void;
  setOpenLeaveModal?: (openLeaveModal: boolean) => void;
  refetchWishlistData?: () => void;
  showEditText?: boolean;
  isDetailsPage?: boolean;
  showDeleteText?: boolean;
  isRecentlyViewed?: boolean;
  isMapOpen?: boolean;
  hideActions?: boolean;
  isPublic?: boolean;
};

const PopoverTrigger = forwardRef<HTMLButtonElement, TPopoverTriggerProps>(
  ({ className: _className, ...props }, ref) => (
    <RawButton
      ref={ref}
      {...props}
      className={`flex items-center justify-center rounded-full bg-[#F1F2F3] w-[48px] h-[48px]`}>
      <GeneralEllipsesHorizontalIcon width={32} height={32} />
    </RawButton>
  ),
);

PopoverTrigger.displayName = 'PopoverTrigger';

export const WishlistsActions = ({
  userRole,
  wishlistId = 0,
  editMode = false,
  setEditMode,
  setOpenRenameModal,
  setOpenDeleteModal,
  setOpenLeaveModal,
  refetchWishlistData,
  showEditText = false,
  isDetailsPage = false,
  showDeleteText = false,
  isRecentlyViewed = false,
  isMapOpen = false,
  hideActions = false,
  isPublic = false,
}: TMobileActionsProps) => {
  const intl = useIntl();
  const router = useRouter();
  const { showToast } = useToast();

  const showBackButton = isDetailsPage || isRecentlyViewed;

  const handleBackButton = () => {
    const url = '/wishlists';
    router.push(url, undefined, { shallow: true });
  };

  const handleEdit = useCallback(() => {
    if (isDetailsPage) {
      setOpenRenameModal?.(true);
    } else {
      setEditMode?.(!editMode);
    }
  }, [editMode, isDetailsPage, setEditMode, setOpenRenameModal]);

  const handleDelete = () => {
    setOpenDeleteModal?.(true);
  };

  const handleIsPublicChange = async () => {
    if (!wishlistId) {
      return;
    }

    try {
      await apiRequest({
        url: `${getCoreApi()}/wishlists/${wishlistId}`,
        method: 'PATCH',
        data: { is_public: !isPublic },
      });

      trackWishlistEditedEvent({
        wishlistID: wishlistId,
        coverPhotoRentalID: null,
        wishlistCreatorID: null,
        wishlistName: null,
        departureDate: null,
        returnDate: null,
        totalAdults: null,
        totalChildren: null,
        isPublic: !isPublic,
      });
      await refetchWishlistData?.();
    } catch {
      showToast(
        EToastVariant.Error,
        intl.formatMessage(
          {
            id: 'HR6BTO',
            defaultMessage: 'Failed to make wishlist {status}',
          },
          {
            status: isPublic ? 'public' : 'private',
          },
        ),
        intl.formatMessage({
          id: 'fHqssj',
          defaultMessage: 'Please try again',
        }),
      );
    }
  };

  return (
    <div
      className={clsx('flex items-center', {
        'pb-4': isDetailsPage,
        'justify-end': !showBackButton,
        'justify-between': showBackButton,
      })}>
      {showBackButton && (
        <IconButton
          icon={GeneralCaretLeftIcon}
          size={EIconButtonSize.Medium}
          variant={EIconButtonVariant.Ghost}
          onClick={handleBackButton}
          className={` ${isMapOpen ? 'z-[60] !bg-white' : '!bg-[#F1F2F3]'}`}
        />
      )}

      {!hideActions && (
        <>
          {showEditText ? (
            <div className="flex gap-2">
              <LinkButton
                label={
                  editMode
                    ? intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' })
                    : intl.formatMessage({ defaultMessage: 'Edit', id: 'wEQDC6' })
                }
                onClick={handleEdit}
              />

              {showDeleteText && (
                <>
                  <LinkButton
                    label={intl.formatMessage({ defaultMessage: 'Clear all', id: 'QW+Q5N' })}
                    onClick={handleDelete}
                  />
                </>
              )}
            </div>
          ) : (
            <Popover
              menuOnly
              components={{
                Trigger: PopoverTrigger,
              }}>
              {canEditDetailsOfWishlist(userRole) && (
                <Popover.Option
                  icon={GeneralEditEdit2Icon}
                  label={(() => {
                    if (isDetailsPage) {
                      return intl.formatMessage({ defaultMessage: 'Rename', id: 'iXNbPf' });
                    }
                    if (editMode) {
                      return intl.formatMessage({ defaultMessage: 'Done', id: 'JXdbo8' });
                    }
                    return intl.formatMessage({ defaultMessage: 'Edit', id: 'wEQDC6' });
                  })()}
                  value={isDetailsPage ? 'rename' : 'edit'}
                  onClick={handleEdit}
                />
              )}
              {(isDetailsPage || isRecentlyViewed) && canDeleteWishlist(userRole) && (
                <Popover.Option
                  icon={GeneralTrashIcon}
                  label={
                    isRecentlyViewed
                      ? intl.formatMessage({ defaultMessage: 'Clear all', id: 'QW+Q5N' })
                      : intl.formatMessage({ defaultMessage: 'Delete', id: 'K3r6DQ' })
                  }
                  value="delete"
                  onClick={() => setOpenDeleteModal?.(true)}
                />
              )}
              {isDetailsPage && canEditPublicStatusOfWishlist(userRole) && (
                <Popover.Option
                  icon={isPublic ? GeneralLockLockIcon : GeneralUserUsersIcon}
                  label={
                    isPublic
                      ? intl.formatMessage({
                          defaultMessage: 'Make private',
                          id: 'Th6aZu',
                        })
                      : intl.formatMessage({
                          defaultMessage: 'Make public',
                          id: 'GaEqBb',
                        })
                  }
                  value="edit_public_status"
                  onClick={handleIsPublicChange}
                />
              )}
              {isDetailsPage && canLeaveWishlist(userRole) && (
                <Popover.Option
                  icon={GeneralMinusSquareIcon}
                  label={intl.formatMessage({ defaultMessage: 'Leave wishlist', id: 'Z3iTa1' })}
                  value="leave"
                  onClick={() => setOpenLeaveModal?.(true)}
                />
              )}
            </Popover>
          )}
        </>
      )}
    </div>
  );
};
